import { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@localagent/ui/components/dialog';
import { Button } from '@localagent/ui/components/button';
import { Input } from '@localagent/ui/components/input';
import { Label } from '@localagent/ui/components/label';
import { toast } from '@localagent/ui/components/sonner';
import { oauthCallbackUrl, useCreateConnection, useAuthenticateApiKey, useAuthenticateClientCredentials, useInitiateOAuth2 } from '../../hooks/useIntegrations';
import { OAuthPopup } from './OAuthPopup';
import type { AuthType } from '../../types/integrations-types';

interface ConnectDialogProps {
  definitionId: string;
  definitionName: string;
  authType: AuthType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (connectionId: string) => void;
}

export function ConnectDialog({ definitionId, definitionName, authType, open, onOpenChange, onSuccess }: ConnectDialogProps) {
  const [connectionName, setConnectionName] = useState(`${definitionName} connection`);
  const [step, setStep] = useState<'name' | 'auth'>('name');
  const [connectionId, setConnectionId] = useState<string | null>(null);

  const [apiKey, setApiKey] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [oauthUrl, setOauthUrl] = useState<string | null>(null);
  const oauthPopupRef = useRef<Window | null>(null);

  const createConnection = useCreateConnection();
  const authApiKey = useAuthenticateApiKey();
  const authCredentials = useAuthenticateClientCredentials();
  const initiateOAuth = useInitiateOAuth2();

  const reset = () => {
    oauthPopupRef.current?.close();
    oauthPopupRef.current = null;
    setStep('name');
    setConnectionId(null);
    setApiKey('');
    setClientId('');
    setClientSecret('');
    setOauthUrl(null);
    setConnectionName(`${definitionName} connection`);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) reset();
    onOpenChange(newOpen);
  };

  const handleCreateConnection = () => {
    if (authType === 'oauth2_authorization_code') {
      const popup = window.open('about:blank', 'oauth_popup', 'width=600,height=700,scrollbars=yes');
      if (!popup) {
        toast.error('Popup blocked — allow popups for this app, then try again.');
        return;
      }
      oauthPopupRef.current = popup;
    }
    createConnection.mutate(
      { definition_id: definitionId, name: connectionName },
      {
        onSuccess: (conn) => {
          setConnectionId(conn.id);
          if (authType === 'oauth2_authorization_code') {
            initiateOAuth.mutate(
              { connectionId: conn.id, redirectUri: oauthCallbackUrl() },
              {
                onSuccess: (data) => setOauthUrl(data.authorization_url),
                onError: (err) => {
                  oauthPopupRef.current?.close();
                  oauthPopupRef.current = null;
                  toast.error(`OAuth initiation failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
                },
              },
            );
          }
          setStep('auth');
        },
        onError: (err) => {
          toast.error(`Failed to create connection: ${err instanceof Error ? err.message : 'Unknown error'}`);
        },
      },
    );
  };

  const handleAuth = () => {
    if (!connectionId) return;
    if (authType === 'api_key') {
      authApiKey.mutate(
        { connectionId, apiKey },
        {
          onSuccess: () => {
            toast.success('Connection created and authenticated');
            handleOpenChange(false);
            onSuccess?.(connectionId);
          },
          onError: (err) => toast.error(`Authentication failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
        },
      );
    } else if (authType === 'oauth2_client_credentials') {
      authCredentials.mutate(
        { connectionId, clientId, clientSecret },
        {
          onSuccess: () => {
            toast.success('Connection created and authenticated');
            handleOpenChange(false);
            onSuccess?.(connectionId);
          },
          onError: (err) => toast.error(`Authentication failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
        },
      );
    }
  };

  const isAuthPending = authApiKey.isPending || authCredentials.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect to {definitionName}</DialogTitle>
          <DialogDescription>
            {step === 'name' ? 'Choose a name for this connection.' : 'Provide authentication credentials.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'name' && (
          <>
            <div className="space-y-2 py-4">
              <Label htmlFor="conn-name">Connection Name</Label>
              <Input
                id="conn-name"
                value={connectionName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConnectionName(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
              <Button onClick={handleCreateConnection} disabled={!connectionName.trim() || createConnection.isPending}>
                {createConnection.isPending ? 'Creating...' : 'Next'}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'auth' && authType === 'api_key' && (
          <>
            <div className="space-y-2 py-4">
              <Label htmlFor="connect-api-key">API Key</Label>
              <Input
                id="connect-api-key"
                type="password"
                placeholder="Enter your API key"
                value={apiKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApiKey(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
              <Button onClick={handleAuth} disabled={!apiKey.trim() || isAuthPending}>
                {isAuthPending ? 'Authenticating...' : 'Authenticate'}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'auth' && authType === 'oauth2_client_credentials' && (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="connect-client-id">Client ID</Label>
                <Input id="connect-client-id" value={clientId} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientId(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="connect-client-secret">Client Secret</Label>
                <Input id="connect-client-secret" type="password" value={clientSecret} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientSecret(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
              <Button onClick={handleAuth} disabled={!clientId.trim() || !clientSecret.trim() || isAuthPending}>
                {isAuthPending ? 'Authenticating...' : 'Authenticate'}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'auth' && authType === 'oauth2_authorization_code' && (
          <>
            <div className="py-4 text-center">
              <p className="text-body text-muted-foreground">
                {oauthUrl ? 'A popup window has been opened for authorization. Complete the flow there.' : 'Preparing authorization...'}
              </p>
            </div>
            <OAuthPopup
              popupWindow={oauthPopupRef.current}
              authUrl={oauthUrl}
              onSuccess={(connId) => {
                toast.success('Connection authorized successfully');
                handleOpenChange(false);
                onSuccess?.(connId);
              }}
              onError={(error) => toast.error(`OAuth failed: ${error}`)}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
